/**
 * Quick instrument switching from the keyboard dock.
 *
 * Keys and Sampler patterns store notes the same way, so a track can flip between a synth voice
 * and one of Russell's one-shots without losing what was played. Drum tracks switch kits.
 * Every function here is pure: callers commit the returned tracks with one pushState.
 */
import { sanitizePattern } from './patternModel';
import { samplerDefaultsForSound } from './sampler';
import { recordableTrackType } from './liveInput';

export const SYNTH_VOICES = [
  { id: 'keys', label: 'Keys' },
  { id: 'strings', label: 'Strings' },
  { id: '808', label: '808' },
  { id: 'fingerbass', label: 'Finger bass' },
  { id: 'pluck', label: 'Pluck' },
];

const BASS_VOICES = new Set(['808', 'fingerbass']);
const BASS_CATEGORIES = new Set(['808', 'bass']);
const DEFAULT_CLIP_NAMES = new Set(['Keys Pattern', 'Sampler Pattern']);

/** The pattern type a choice needs: synth -> keys, sample -> sampler, kit -> drums. */
export function choiceType(choice) {
  if (choice?.kind === 'kit') return 'drums';
  if (choice?.kind === 'sample') return 'sampler';
  return 'keys';
}

/** True when the armed track can take this choice in place (no new track needed). */
export function choiceFitsTrack(choice, track) {
  const type = recordableTrackType(track);
  if (!type) return false;
  return choiceType(choice) === 'drums' ? type === 'drums' : type === 'keys' || type === 'sampler';
}

/** What the armed track plays right now, for the dock's "Playing" label. */
export function currentChoiceForTrack(track, { findSound = () => null, kits = [] } = {}) {
  const type = recordableTrackType(track);
  if (!type) return null;
  const clip = (track.clips || []).find((item) => item.isPattern && item.patternType === type);
  const pattern = clip?.pattern || {};
  if (type === 'drums') {
    const kit = kits.find((item) => item.id === pattern.kitId) || kits[0] || null;
    return kit ? { kind: 'kit', id: kit.id, label: kit.name } : null;
  }
  if (type === 'sampler') {
    const sound = pattern.sampleId ? findSound(pattern.sampleId) : null;
    return sound ? { kind: 'sample', id: sound.id, category: sound.category, label: sound.name, sound } : { kind: 'sample', id: null, label: 'Sound missing' };
  }
  const voice = SYNTH_VOICES.find((item) => item.id === pattern.voice) || SYNTH_VOICES[0];
  return { kind: 'synth', id: voice.id, label: voice.label };
}

function instrumentFor(choice) {
  if (choice.kind === 'kit') return 'drums';
  if (choice.kind === 'synth') return BASS_VOICES.has(choice.id) ? 'bass' : 'melody';
  return BASS_CATEGORIES.has(String(choice.sound?.category || '').toLowerCase()) ? 'bass' : 'melody';
}

function switchPattern(clip, choice, kits) {
  const pattern = clip.pattern || {};
  if (choice.kind === 'kit') {
    const kit = kits.find((item) => item.id === choice.id) || null;
    const rows = (pattern.rows || []).map((row) => ({ ...row, soundId: kit?.rows?.find((item) => item.id === row.rowId)?.sounds?.[0]?.id || null }));
    return { patternType: 'drums', pattern: sanitizePattern('drums', { ...pattern, kitId: choice.id, rows }) };
  }
  if (choice.kind === 'synth') {
    const voice = choice.id;
    const mono = voice === '808' ? true : Boolean(pattern.mono && clip.patternType === 'keys');
    return { patternType: 'keys', pattern: sanitizePattern('keys', { ...pattern, voice, mono, glide: voice === '808' ? (pattern.glide || 80) : (mono ? pattern.glide : 0) }) };
  }
  return { patternType: 'sampler', pattern: sanitizePattern('sampler', { ...pattern, ...samplerDefaultsForSound(choice.sound) }) };
}

/**
 * Apply an instrument choice to every pattern clip of one track, and to clips linked to them on
 * other tracks (same loop_id), so linked copies never disagree about what they play.
 */
export function applyInstrumentChoice(tracks, trackId, choice, { kits = [] } = {}) {
  const track = (tracks || []).find((item) => item.id === trackId);
  if (!track || !choiceFitsTrack(choice, track)) return tracks;
  const fromTypes = choiceType(choice) === 'drums' ? new Set(['drums']) : new Set(['keys', 'sampler']);
  const loopIds = new Set((track.clips || []).filter((clip) => clip.isPattern && fromTypes.has(clip.patternType)).map((clip) => clip.loop_id));
  const instrument = instrumentFor(choice);
  return tracks.map((item) => {
    const touched = (item.clips || []).some((clip) => clip.isPattern && loopIds.has(clip.loop_id));
    if (!touched) return item;
    const clips = item.clips.map((clip) => {
      if (!clip.isPattern || !loopIds.has(clip.loop_id)) return clip;
      const next = switchPattern(clip, choice, kits);
      const name = DEFAULT_CLIP_NAMES.has(clip.name) ? (next.patternType === 'sampler' ? 'Sampler Pattern' : next.patternType === 'keys' ? 'Keys Pattern' : clip.name) : clip.name;
      return { ...clip, ...next, name };
    });
    const renamed = DEFAULT_CLIP_NAMES.has(item.name) ? (choiceType(choice) === 'sampler' ? 'Sampler Pattern' : 'Keys Pattern') : item.name;
    return { ...item, clips, name: choiceType(choice) === 'drums' ? item.name : renamed, instrument: item.id === trackId ? instrument : item.instrument };
  });
}

/** Step to the previous or next sound inside the current group (wraps around). */
export function cycleChoice(current, direction, { kits = [], oneShots = () => [] } = {}) {
  const step = direction < 0 ? -1 : 1;
  const wrap = (list, index) => list[(index + step + list.length) % list.length];
  if (!current || current.kind === 'synth') {
    const index = Math.max(0, SYNTH_VOICES.findIndex((item) => item.id === current?.id));
    const voice = wrap(SYNTH_VOICES, current ? index : -step);
    return { kind: 'synth', id: voice.id, label: voice.label };
  }
  if (current.kind === 'kit') {
    if (!kits.length) return current;
    const kit = wrap(kits, Math.max(0, kits.findIndex((item) => item.id === current.id)));
    return { kind: 'kit', id: kit.id, label: kit.name };
  }
  const list = oneShots(current.category);
  if (!list.length) return current;
  const sound = wrap(list, Math.max(0, list.findIndex((item) => item.id === current.id)));
  return { kind: 'sample', id: sound.id, category: sound.category, label: sound.name, sound };
}
